import type {
  Attrs,
  MarkSpec,
  MarkType,
  Node as ProsemirrorNode,
  ResolvedPos,
  Schema,
} from 'prosemirror-model';
import type { EditorState, SelectionRange, Transaction } from 'prosemirror-state';
import { NodeSelection, TextSelection } from 'prosemirror-state';

import type { ProsemirrorDispatch } from '../editor/types';

export const markSpecs = <U extends Record<string, MarkSpec>>(v: U): U => v;

const isActiveMark = (
  state: EditorState,
  $from: ResolvedPos,
  $to: ResolvedPos,
  empty: boolean,
  markType: MarkType,
) => {
  if (state.selection instanceof NodeSelection) {
    return state.selection.node.marks.some(mark => mark.type.name === markType.name);
  }
  const selection = TextSelection.between($from, $to);
  if (selection.empty) {
    return !!markType.isInSet(state.storedMarks || selection.$from.marks());
  } else {
    const maxPos = selection.$to.pos;
    let pos = selection.$from.pos - selection.$from.textOffset;
    let active = true;
    let node = state.doc.nodeAt(pos);
    if (!node) return false;
    while (node) {
      active = active && node.marks.some(mark => mark.type.name === markType.name);
      if (!active) return false;
      if (node.nodeSize + pos < maxPos) {
        pos += node.nodeSize;
        node = state.doc.nodeAt(pos);
        while (pos < maxPos && (!node || (!node.isText && node.isTextblock))) {
          pos += 1;
          node = state.doc.nodeAt(pos);
        }
      } else {
        node = null;
      }
    }
    return active;
  }
};

// This comes from markApplies in prosemirror-commands
const markApplies = (doc: ProsemirrorNode, ranges: readonly SelectionRange[], type: MarkType) => {
  for (let i = 0; i < ranges.length; i++) {
    const { $from, $to } = ranges[i];
    let can = $from.depth === 0 ? doc.type.allowsMarkType(type) : false;
    doc.nodesBetween($from.pos, $to.pos, node => {
      if (can) return false;
      can = node.inlineContent && node.type.allowsMarkType(type);
      return true;
    });
    if (can) return true;
  }
  return false;
};

/*
 * @desc This code is slightly modified version of toggleMark
 * from prosemirror-commands that uses our fn to check if selection
 * contains mark
 */

export function toggleMark(markType: MarkType, attrs?: Record<string, string>) {
  return (state: EditorState, dispatch?: ProsemirrorDispatch) => {
    // @ts-ignore
    const { empty, $cursor, ranges } = state.selection;
    if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) {
      return false;
    }
    if (!dispatch) return true;

    if ($cursor) {
      if (markType.isInSet(state.storedMarks || $cursor.marks())) {
        dispatch(state.tr.removeStoredMark(markType));
      } else {
        dispatch(state.tr.addStoredMark(markType.create(attrs)));
      }
    } else {
      let has = false;
      const tr = state.tr;
      for (let i = 0; !has && i < ranges.length; i++) {
        const { $from, $to } = ranges[i];
        // has = state.doc.rangeHasMark($from.pos, $to.pos, markType);
        has = isActiveMark(state, $from, $to, false, markType);
      }
      for (let i = 0; i < ranges.length; i++) {
        const { $from, $to } = ranges[i];
        if (has) {
          tr.removeMark($from.pos, $to.pos, markType);
        } else {
          let from = $from.pos;
          let to = $to.pos;
          const start = $from.nodeAfter;
          const end = $to.nodeBefore;

          const spaceStart = start && start.isText ? /^\s*/.exec(start.text!)![0].length : 0;
          const spaceEnd = end && end.isText ? /\s*$/.exec(end.text!)![0].length : 0;
          if (from + spaceStart < to) {
            from += spaceStart;
            to -= spaceEnd;
          }
          tr.addMark(from, to, markType.create(attrs));
        }
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

export function addMark(markType: MarkType, attrs?: Record<string, string>) {
  return (state: EditorState, dispatch?: ProsemirrorDispatch) => {
    // @ts-ignore
    const { empty, $cursor, ranges } = state.selection;
    if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) {
      return false;
    }
    if (!dispatch) return true;

    if ($cursor) {
      dispatch(state.tr.addStoredMark(markType.create(attrs)));
    } else {
      const tr = state.tr;
      for (let i = 0; i < ranges.length; i++) {
        const { $from, $to } = ranges[i];
        let from = $from.pos;
        let to = $to.pos;
        const start = $from.nodeAfter;
        const end = $to.nodeBefore;

        const spaceStart = start && start.isText ? /^\s*/.exec(start.text!)![0].length : 0;
        const spaceEnd = end && end.isText ? /\s*$/.exec(end.text!)![0].length : 0;
        if (from + spaceStart < to) {
          from += spaceStart;
          to -= spaceEnd;
        }
        tr.addMark(from, to, markType.create(attrs));
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

export function removeMark(markType: MarkType) {
  return (state: EditorState, dispatch?: ProsemirrorDispatch) => {
    // @ts-ignore
    const { empty, $cursor, ranges } = state.selection;
    if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) {
      return false;
    }
    if (!dispatch) return true;

    if ($cursor) {
      dispatch(state.tr.removeStoredMark(markType));
    } else {
      const tr = state.tr;
      for (let i = 0; i < ranges.length; i++) {
        const { $from, $to } = ranges[i];
        tr.removeMark($from.pos, $to.pos, markType);
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

export function getAttrs(state: EditorState, markType: MarkType): Attrs | null {
  const { $from, $to } = state.selection;
  if (state.storedMarks) {
    if (markType.isInSet(state.storedMarks)) {
      return state.storedMarks.find(mark => mark.type.name === markType.name)?.attrs || null;
    }
  }

  const selection = TextSelection.between($from, $to);
  const pos = selection.$from.pos - selection.$from.textOffset;
  const node = state.doc.nodeAt(pos);

  if (node) {
    const mark = node.marks.find(mark => mark.type.name === markType.name);
    if (mark) return mark.attrs;
  }

  return null;
}

export abstract class MarkService<S extends Schema> {
  constructor(protected mark: S['marks'][string]) {}

  public toggle<ES extends EditorState>(
    state: ES,
    dispatch?: (tr: Transaction) => void,
    attrs?: Record<string, string>,
  ) {
    return toggleMark(this.mark, attrs)(state, dispatch);
  }

  public add<ES extends EditorState>(
    state: ES,
    dispatch?: (tr: Transaction) => void,
    attrs?: Record<string, string>,
  ) {
    return addMark(this.mark, attrs)(state, dispatch);
  }

  public remove<ES extends EditorState>(state: ES, dispatch?: (tr: Transaction) => void) {
    return removeMark(this.mark)(state, dispatch);
  }

  public isActive(state: EditorState) {
    const { $from, $to, empty } = state.selection;
    return isActiveMark(state, $from, $to, empty, this.mark);
  }

  public getAttrsIfActive(state: EditorState): Attrs | null {
    if (!this.isActive(state)) return null;
    return getAttrs(state, this.mark);
  }
}
